import React, { useEffect, useState } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { Confirm } from './../../Commons/Modals/Confirm';
import { PremiumQR } from './../../Commons/PremiumQR';
import { 
    GetThemes, 
    GetTheme,
    SaveTheme, 
    DeleteTheme, 
    GetConfig, 
    SaveConfig, 
    UploadMedia,
    PrepareScreen,
    ProyectarScreen,
    StartGame,
    StopGame,
    ResetGame,
    Jump,
    GetHistory
} from './../../../utilities/BL/Dinosaur';

// Componente Drag & Drop para subida de archivos
const DragAndDropField = ({ label, targetField, type, value, onUpload, getMediaUrl }) => {
    const [isDragOver, setIsDragOver] = useState(false);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            onUpload(file, type, targetField);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            onUpload(file, type, targetField);
        }
    };

    return (
        <div className="mb-3">
            {label && <label className="form-label fw-bold text-dark">{label}:</label>}
            <div 
                className={`border border-2 border-dashed rounded-3 p-3 text-center cursor-pointer transition ${isDragOver ? 'border-primary bg-light' : 'border-secondary bg-white'}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById(`file-input-${targetField}`).click()}
            >
                <i className="bi bi-cloud-arrow-up-fill text-primary fs-3"></i>
                <p className="small text-muted mb-0 mt-1">Arrastra y suelta tu archivo aquí, o haz clic para examinar</p>
                <input 
                    id={`file-input-${targetField}`}
                    type="file" 
                    className="d-none"
                    onChange={handleFileChange}
                />
            </div>
            {value && (
                <div className="mt-2 text-center bg-light p-2 rounded border position-relative">
                    {type !== 'video' && (value.endsWith('.png') || value.endsWith('.jpg') || value.endsWith('.jpeg')) ? (
                        <img src={getMediaUrl(value)} alt="Preview" style={{ maxHeight: '60px', objectFit: 'contain' }} />
                    ) : (
                        <span className="small text-truncate d-block text-dark">{value.split('/').pop()}</span>
                    )}
                </div>
            )}
        </div>
    );
};

export const Dinosaur = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();
    const [isLoading, setIsLoading] = useState(false);
    const [lastRefresh, setLastRefresh] = useState('');

    const [themesList, setThemesList] = useState([]);
    const [historyList, setHistoryList] = useState([]);
    
    // Control de Acordeones
    const [isMainExpanded, setIsMainExpanded] = useState(true);
    const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);

    // Modales
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({ message: '', action: null, textYes: '', textNo: 'Cancelar' });
    const [showThemeModal, setShowThemeModal] = useState(false);
    const [showQRModal, setShowQRModal] = useState(false);
    const [qrTargetUrl, setQrTargetUrl] = useState('');
    const [tvTargetUrl, setTvTargetUrl] = useState('');
    const [qrTitle, setQrTitle] = useState('');

    // Configuración Estadio (ID 1 Fijo)
    const [mainConfig, setMainConfig] = useState({ 
        targetScreenId: 1, 
        themeId: '', 
        winProbability: 30, 
        baseSpeed: 8, 
        celebrationTimeMs: 6000, 
        scale: 100,
        playMode: 2, 
        playDuration: 20 
    });

    // Estado del Tema en Edición
    const defaultThemeState = {
        id: 0,
        name: '',
        brandLogoUrl: '',
        sponsorLogoUrl: '',
        obstacleImages: [],
        characterImage: '',
        textColors: '#333333',
        borderColors: '#333333',
        coverBackgroundType: 'color',
        coverBackgroundColor: '#e5e7eb',
        coverBackgroundUrl: '',
        coverTitle: 'DINOSAURIO',
        coverTitleColor: '#ffffff',
        gameFrameBackgroundType: 'color',
        gameFrameBackgroundColor: '#f7f9fc',
        gameFrameBackgroundUrl: '',
        fanBackgroundType: 'color',
        fanBackgroundColor: '#e5e7eb',
        fanBackgroundUrl: '',
        fanButtonType: 'color',
        fanButtonColor: '#ffc107',
        fanButtonUrl: ''
    };
    const [themeForm, setThemeForm] = useState(defaultThemeState);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        setIsLoading(true);
        try {
            const themesRes = await GetThemes();
            if (themesRes.success && themesRes.data) setThemesList(themesRes.data);

            const configRes = await GetConfig(1);
            if (configRes.success && configRes.data) {
                setMainConfig({
                    ...configRes.data,
                    themeId: configRes.data.themeId || ''
                });
            }

            const historyRes = await GetHistory(1);
            if (historyRes.success && historyRes.data) setHistoryList(historyRes.data);

            setLastRefresh(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
        } catch (error) {
            showMessage('Error al conectar con la API', MESSAGE_TYPE.ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    const getMediaUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http://') || path.startsWith('https://')) return path;
        const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        return `${apiBase}${path}`;
    };

    // ==========================================
    // AUTO-GUARDADO DE CONFIGURACIÓN
    // ==========================================
    const autoSaveConfig = async (newConfig) => {
        showMessage('Guardando cambios...', MESSAGE_TYPE.INFO);
        const parsed = {
            ...newConfig,
            themeId: newConfig.themeId ? Number(newConfig.themeId) : null
        };
        const result = await SaveConfig(parsed);
        if (result.success) {
            showMessage('✅ Configuración actualizada', MESSAGE_TYPE.SUCCESS);
        } else {
            showMessage('Error al guardar configuración', MESSAGE_TYPE.ERROR);
        }
    };

    const updateMainConfig = (field, value) => {
        const updated = { ...mainConfig, [field]: value };
        setMainConfig(updated);
        autoSaveConfig(updated);
    };

    // ==========================================
    // CONTROLES DE LA DINÁMICA
    // ==========================================
    const handlePrepare = async () => {
        if (!mainConfig.themeId) {
            showMessage('⚠️ Selecciona un tema primero', MESSAGE_TYPE.WARNING);
            return;
        }
        setIsLoading(true);
        const result = await PrepareScreen(1);
        setIsLoading(false);
        if (result.success) {
            showMessage('📺 Portada proyectada en pantalla.', MESSAGE_TYPE.SUCCESS);
        } else {
            showMessage('Error al proyectar portada', MESSAGE_TYPE.ERROR);
        }
    };

    const handleProyectarGame = async () => {
        if (!mainConfig.themeId) {
            showMessage('⚠️ Selecciona un tema primero', MESSAGE_TYPE.WARNING);
            return;
        }
        setIsLoading(true);
        const result = await ProyectarScreen(1);
        setIsLoading(false);
        if (result.success) {
            showMessage('🎮 Juego proyectado en pantalla (listo para jugar).', MESSAGE_TYPE.SUCCESS);
        } else {
            showMessage('Error al proyectar juego', MESSAGE_TYPE.ERROR);
        }
    };

    // Controles de Emergencia del Admin
    const handleStart = async (currentQuestion) => {
        setIsLoading(true);
        const result = await StartGame(1, mainConfig.baseSpeed || 8, mainConfig.playMode || 1, mainConfig.playDuration || 30);
        setIsLoading(false);
        if (result.success) {
            showMessage('🏁 Juego iniciado en pantalla', MESSAGE_TYPE.SUCCESS);
        } else {
            showMessage('Error al iniciar juego', MESSAGE_TYPE.ERROR);
        }
    };

    const handleJump = async () => {
        const result = await Jump(1, 'Administrador');
        if (result.success) {
            showMessage('⬆️ Salto enviado a la pantalla', MESSAGE_TYPE.SUCCESS);
        }
    };

    const handleStop = async () => {
        setIsLoading(true);
        const result = await StopGame(1);
        setIsLoading(false);
        if (result.success) {
            showMessage('🛑 Partida detenida', MESSAGE_TYPE.SUCCESS);
        }
    };

    const handleReset = async () => {
        setIsLoading(true);
        const result = await ResetGame(1);
        setIsLoading(false);
        if (result.success) {
            showMessage('🔄 Dinámica restablecida', MESSAGE_TYPE.SUCCESS);
            loadInitialData();
        }
    };

    // ==========================================
    // GESTIÓN DE TEMAS (CRUD)
    // ==========================================
    const openThemeModal = (theme = null) => {
        if (theme) {
            setThemeForm({
                id: theme.id,
                name: theme.name,
                brandLogoUrl: theme.brandLogoUrl || '',
                sponsorLogoUrl: theme.sponsorLogoUrl || '',
                obstacleImages: theme.obstacleImages || [],
                characterImage: theme.characterImage || '',
                textColors: theme.textColors || '#333333',
                borderColors: theme.borderColors || '#333333',
                coverBackgroundType: theme.coverBackgroundType || 'color',
                coverBackgroundColor: theme.coverBackgroundColor || '#e5e7eb',
                coverBackgroundUrl: theme.coverBackgroundUrl || '',
                coverTitle: theme.coverTitle || 'DINOSAURIO',
                coverTitleColor: theme.coverTitleColor || '#ffffff',
                gameFrameBackgroundType: theme.gameFrameBackgroundType || 'color',
                gameFrameBackgroundColor: theme.gameFrameBackgroundColor || '#f7f9fc',
                gameFrameBackgroundUrl: theme.gameFrameBackgroundUrl || '',
                fanBackgroundType: theme.fanBackgroundType || 'color',
                fanBackgroundColor: theme.fanBackgroundColor || '#e5e7eb',
                fanBackgroundUrl: theme.fanBackgroundUrl || '',
                fanButtonType: theme.fanButtonType || 'color',
                fanButtonColor: theme.fanButtonColor || '#ffc107',
                fanButtonUrl: theme.fanButtonUrl || ''
            });
        } else {
            setThemeForm(defaultThemeState);
        }
        setShowThemeModal(true);
    };

    const handleUploadMedia = async (file, type, targetField) => {
        showMessage('Comprimiendo y subiendo...', MESSAGE_TYPE.INFO);
        const result = await UploadMedia(file, type);
        if (result.success && result.url) {
            showMessage('✅ Archivo cargado', MESSAGE_TYPE.SUCCESS);
            if (targetField === 'obstacleImages') {
                setThemeForm(prev => ({
                    ...prev,
                    obstacleImages: [...prev.obstacleImages, result.url]
                }));
            } else {
                setThemeForm(prev => ({
                    ...prev,
                    [targetField]: result.url
                }));
            }
        } else {
            showMessage('Error al subir archivo', MESSAGE_TYPE.ERROR);
        }
    };

    const handleSaveTheme = async () => {
        if (!themeForm.name.trim()) {
            showMessage('El nombre del tema es obligatorio', MESSAGE_TYPE.ERROR);
            return;
        }
        setIsLoading(true);
        const result = await SaveTheme(themeForm);
        setIsLoading(false);
        if (result.success) {
            showMessage('🎉 Tema guardado correctamente', MESSAGE_TYPE.SUCCESS);
            setShowThemeModal(false);
            loadInitialData();
        } else {
            showMessage(result.message || 'Error al guardar tema', MESSAGE_TYPE.ERROR);
        }
    };

    const handleDeleteTheme = (id, name) => {
        setConfirmConfig({
            message: `¿Estás seguro de que deseas eliminar el tema "${name}"?`,
            textYes: 'Eliminar',
            textNo: 'Cancelar',
            action: async () => {
                setIsLoading(true);
                const result = await DeleteTheme(id);
                setIsLoading(false);
                if (result.success) {
                    showMessage('Tema eliminado', MESSAGE_TYPE.SUCCESS);
                    loadInitialData();
                } else {
                    showMessage(result.message || 'Error al eliminar', MESSAGE_TYPE.ERROR);
                }
                setShowConfirmModal(false);
            }
        });
        setShowConfirmModal(true);
    };

    const removeObstacle = (index) => {
        setThemeForm(prev => ({
            ...prev,
            obstacleImages: prev.obstacleImages.filter((_, i) => i !== index)
        }));
    };

    return (
        <SecondaryBase 
            title="Dinosaurio" 
            getOutOfTheDynamic={getOutOfTheDynamic}
            rightComponent={
                <button className="btn btn-outline-secondary btn-sm" onClick={loadInitialData} disabled={isLoading}>
                    <i className="bi bi-arrow-clockwise me-1"></i> Actualizar ({lastRefresh})
                </button>
            }
        >
            <div className="container-fluid py-3 px-4">
                {/* 1. SECCIÓN PANTALLA PRINCIPAL EN DOS COLUMNAS (Estilo Tragamonedas) */}
                <div className="card shadow-sm border-0 mb-4 rounded-3 overflow-hidden text-dark">
                    <div 
                        className="card-header bg-dark text-white d-flex justify-content-between align-items-center cursor-pointer py-3"
                        onClick={() => setIsMainExpanded(!isMainExpanded)}
                    >
                        <h5 className="mb-0 fw-bold"><i className="bi bi-display me-2 text-warning"></i> Pantalla del estadio</h5>
                        <button className="btn btn-success btn-sm font-semibold" onClick={(e) => { e.stopPropagation(); handleProyectarGame(); }}>
                            Proyectar dinosaurio en pantalla
                        </button>
                    </div>

                    {isMainExpanded && (
                        <div className="card-body bg-light-subtle">
                            <div className="row g-4">
                                {/* Columna Izquierda: Configuración del juego */}
                                <div className="col-md-7 border-end">
                                    {/* Selector de Tema */}
                                    <div className="mb-4">
                                        <label className="form-label fw-bold text-dark"><i className="bi bi-palette me-1 text-primary"></i> Tema de Patrocinador:</label>
                                        <div className="input-group">
                                            <select 
                                                className="form-select border-primary"
                                                value={mainConfig.themeId}
                                                onChange={(e) => updateMainConfig('themeId', e.target.value)}
                                            >
                                                <option value="">-- Selecciona el Tema --</option>
                                                {themesList.map(t => (
                                                    <option key={t.id} value={t.id}>{t.name}</option>
                                                ))}
                                            </select>
                                            <button className="btn btn-primary" onClick={() => openThemeModal()}>
                                                <i className="bi-plus-lg"></i>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Modos de Juego */}
                                    <div className="mb-4">
                                        <label className="form-label fw-bold text-dark"><i className="bi bi-controller me-1"></i> Modo de juego:</label>
                                        <div className="d-flex gap-2 mb-2">
                                            <button 
                                                className={`btn ${mainConfig.playMode === 2 ? 'btn-primary' : 'btn-outline-secondary'} flex-grow-1 font-semibold`}
                                                onClick={() => updateMainConfig('playMode', 2)}
                                            >
                                                Con tiempo límite
                                            </button>
                                            <button 
                                                className={`btn ${mainConfig.playMode === 1 ? 'btn-primary' : 'btn-outline-secondary'} flex-grow-1 font-semibold`}
                                                onClick={() => updateMainConfig('playMode', 1)}
                                            >
                                                Sin tiempo (hasta que pierda)
                                            </button>
                                        </div>

                                        {mainConfig.playMode === 2 && (
                                            <div className="p-3 border rounded bg-white mt-2">
                                                <label className="form-label fw-bold small text-muted">Duración de la partida (segundos):</label>
                                                <div className="d-flex align-items-center gap-2">
                                                    <input 
                                                        type="number" 
                                                        className="form-control w-25" 
                                                        value={mainConfig.playDuration} 
                                                        onChange={(e) => updateMainConfig('playDuration', Number(e.target.value))} 
                                                    />
                                                    <span className="text-muted small">segundos</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Velocidad */}
                                    <div className="mb-4">
                                        <label className="form-label fw-bold text-dark">Velocidad de obstáculos:</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <button className="btn btn-outline-secondary btn-sm" onClick={() => updateMainConfig('baseSpeed', Math.max(4, mainConfig.baseSpeed - 1))}><i className="bi bi-dash"></i></button>
                                            <span className="fs-5 fw-bold px-3 border rounded bg-white text-dark">{mainConfig.baseSpeed} px/f</span>
                                            <button className="btn btn-outline-secondary btn-sm" onClick={() => updateMainConfig('baseSpeed', Math.min(25, mainConfig.baseSpeed + 1))}><i className="bi bi-plus"></i></button>
                                            <span className="text-muted small ms-2">(Recomendado: 8)</span>
                                        </div>
                                    </div>

                                    {/* Tamaño de la Máquina (Zoom) */}
                                    <div className="mb-3">
                                        <label className="form-label fw-bold text-dark">Tamaño del Juego (Zoom):</label>
                                        <div className="d-flex align-items-center gap-3">
                                            <input 
                                                type="range" 
                                                className="form-range flex-grow-1" 
                                                min="30" 
                                                max="300" 
                                                step="5"
                                                value={mainConfig.scale} 
                                                onChange={(e) => updateMainConfig('scale', Number(e.target.value))}
                                            />
                                            <div className="d-flex align-items-center gap-1" style={{ minWidth: '100px' }}>
                                                <input 
                                                    type="number" 
                                                    className="form-control form-control-sm text-center fw-bold" 
                                                    min="30" 
                                                    max="300"
                                                    value={mainConfig.scale} 
                                                    onChange={(e) => {
                                                        const val = Math.min(300, Math.max(30, Number(e.target.value)));
                                                        updateMainConfig('scale', val);
                                                    }}
                                                    style={{ width: '65px' }}
                                                />
                                                <span className="fw-bold text-dark">%</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Columna Derecha: Controles de conexión y de emergencia */}
                                <div className="col-md-5 d-flex flex-column justify-content-between">
                                    <div>
                                        <h6 className="fw-bold border-bottom pb-2 mb-3 text-secondary">Preparación</h6>
                                        <button 
                                            className="btn btn-outline-primary w-100 mb-2 py-2 font-semibold"
                                            onClick={() => {
                                                setQrTitle('Conexión Celular Aficionado');
                                                setQrTargetUrl(`${window.location.origin}/dinosaur?screenId=1`);
                                                setTvTargetUrl(`${window.location.origin}/screen`);
                                                setShowQRModal(true);
                                            }}
                                        >
                                            <i className="bi bi-qr-code me-2"></i> Ver instrucciones de conexión
                                        </button>
                                        <button 
                                            className="btn btn-warning w-100 mb-4 py-2 font-semibold"
                                            onClick={handlePrepare}
                                            disabled={isLoading}
                                        >
                                            <i className="bi bi-projector-fill me-2"></i> Mostrar portada
                                        </button>
                                    </div>

                                    <div>
                                        <h6 className="fw-bold border-bottom pb-2 mb-3 text-secondary">Controles de emergencia</h6>
                                        <div className="d-flex flex-column gap-2">
                                            <button className="btn btn-success py-2 font-semibold text-white border-0 shadow-sm" onClick={handleStart} disabled={isLoading}>
                                                <i className="bi bi-play-fill me-1"></i> Iniciar partida
                                            </button>
                                            <button className="btn btn-primary py-2 font-semibold text-white border-0 shadow-sm" onClick={handleJump}>
                                                <i className="bi bi-arrow-up-circle-fill me-1"></i> Hacer saltar personaje
                                            </button>
                                            <button className="btn btn-danger py-2 font-semibold text-white border-0 shadow-sm" onClick={handleStop} disabled={isLoading}>
                                                <i className="bi bi-stop-fill me-1"></i> Detener partida
                                            </button>
                                            <button className="btn btn-outline-warning py-2 font-semibold border-2" onClick={handleReset} disabled={isLoading}>
                                                <i className="bi bi-arrow-clockwise me-1"></i> Reiniciar partida
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* TABLA DE HISTORIAL DE PARTIDAS */}
                <div className="card shadow-sm border-0 mb-4 rounded-3 overflow-hidden text-dark">
                    <div 
                        className="card-header bg-secondary text-white d-flex justify-content-between align-items-center cursor-pointer py-3"
                        onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                    >
                        <h5 className="mb-0 fw-bold"><i className="bi bi-clock-history me-2"></i> Historial de Partidas Recientes</h5>
                        <i className={`bi bi-chevron-${isHistoryExpanded ? 'up' : 'down'} fs-5`}></i>
                    </div>

                    {isHistoryExpanded && (
                        <div className="card-body p-0">
                            <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                <table className="table table-hover table-striped mb-0 text-dark">
                                    <thead className="table-dark" style={{ position: 'sticky', top: 0, zIndex: 5 }}>
                                        <tr>
                                            <th># partida</th>
                                            <th>Jugador (Admin o Aficionado)</th>
                                            <th>Tema</th>
                                            <th>Puntuación</th>
                                            <th>Fecha</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historyList.length === 0 ? (
                                            <tr>
                                                <td colSpan="5" className="text-center text-muted py-4">No hay partidas registradas en el historial.</td>
                                            </tr>
                                        ) : (
                                            historyList.slice(0, 100).map(h => (
                                                <tr key={h.id}>
                                                    <td>{h.id}</td>
                                                    <td className="fw-bold">{h.playerName}</td>
                                                    <td><span className="badge bg-secondary">{h.themeName}</span></td>
                                                    <td className="fw-bold text-primary">{h.score} pts</td>
                                                    <td>{new Date(h.playDate).toLocaleString()}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* LISTADO Y ADMINISTRACIÓN DE TEMAS DE MARCA */}
                <div className="card shadow-sm border-0 rounded-3 overflow-hidden text-dark">
                    <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center py-3">
                        <h5 className="mb-0 fw-bold"><i className="bi bi-tags-fill me-2"></i> Catálogo de Temas</h5>
                        <button className="btn btn-light btn-sm fw-bold text-primary" onClick={() => openThemeModal()}>
                            <i className="bi bi-plus-circle-fill me-1"></i> Crear Nuevo Tema
                        </button>
                    </div>

                    <div className="card-body bg-light-subtle">
                        <div className="row g-4">
                            {themesList.map(theme => (
                                <div key={theme.id} className="col-md-4">
                                    <div className="card h-100 shadow-sm border-2">
                                        <div className="card-body d-flex flex-column text-dark">
                                            <h6 className="card-title fw-bold fs-5 text-primary border-bottom pb-2">{theme.name}</h6>
                                            <div className="small text-muted mb-3 flex-grow-1">
                                                <p className="mb-1"><strong>Logotipo:</strong> {theme.brandLogoUrl ? '✅ Subido' : '❌ Falta'}</p>
                                                <p className="mb-1"><strong>Personaje:</strong> {theme.characterImage ? '✅ Subido' : '❌ Falta'}</p>
                                                <p className="mb-1"><strong>Obstáculos:</strong> {theme.obstacleImages?.length || 0} cargados</p>
                                                <p className="mb-1"><strong>Fondo Proyección:</strong> {theme.gameFrameBackgroundType === 'color' ? `Color (${theme.gameFrameBackgroundColor})` : 'Imagen/Video'}</p>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button className="btn btn-outline-primary btn-sm w-50" onClick={() => openThemeModal(theme)}>
                                                    <i className="bi bi-pencil-square me-1"></i> Editar
                                                </button>
                                                <button className="btn btn-outline-danger btn-sm w-50" onClick={() => handleDeleteTheme(theme.id, theme.name)}>
                                                    <i className="bi bi-trash-fill me-1"></i> Eliminar
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* MODAL: CREAR/EDITAR TEMA */}
            {showThemeModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050, overflowY: 'auto' }}>
                    <div className="modal-dialog modal-lg text-dark">
                        <div className="modal-content border-0 shadow-lg rounded-3">
                            <div className="modal-header bg-dark text-white py-3">
                                <h5 className="modal-title fw-bold"><i className="bi bi-sliders me-2"></i> {themeForm.id === 0 ? 'Crear' : 'Editar'} Tema de Personalización</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowThemeModal(false)}></button>
                            </div>
                            <div className="modal-body bg-light p-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label className="form-label fw-bold">Nombre del Patrocinio/Marca (ej. Corona):</label>
                                        <input 
                                            type="text" 
                                            className="form-control border-dark" 
                                            value={themeForm.name} 
                                            onChange={(e) => setThemeForm({ ...themeForm, name: e.target.value })} 
                                        />
                                    </div>

                                    {/* Assets de Marca */}
                                    <div className="col-md-6">
                                        <DragAndDropField 
                                            label="Logotipo Principal (Arriba del cuadro)" 
                                            targetField="brandLogoUrl" 
                                            type="logo" 
                                            value={themeForm.brandLogoUrl} 
                                            onUpload={handleUploadMedia} 
                                            getMediaUrl={getMediaUrl} 
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <DragAndDropField 
                                            label="Logotipo Patrocinador (Portada)" 
                                            targetField="sponsorLogoUrl" 
                                            type="logo" 
                                            value={themeForm.sponsorLogoUrl} 
                                            onUpload={handleUploadMedia} 
                                            getMediaUrl={getMediaUrl} 
                                        />
                                    </div>

                                    {/* Personaje y Elementos */}
                                    <div className="col-md-6">
                                        <DragAndDropField 
                                            label="Imagen Personaje (Dinosaurio)" 
                                            targetField="characterImage" 
                                            type="character" 
                                            value={themeForm.characterImage} 
                                            onUpload={handleUploadMedia} 
                                            getMediaUrl={getMediaUrl} 
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Subir Obstáculo (Agregar al catálogo):</label>
                                        <DragAndDropField 
                                            label={null} 
                                            targetField="obstacleImages" 
                                            type="obstacle" 
                                            value="" 
                                            onUpload={handleUploadMedia} 
                                            getMediaUrl={getMediaUrl} 
                                        />
                                    </div>

                                    {/* Visualizador de catálogo de obstáculos */}
                                    <div className="col-12">
                                        <label className="form-label fw-bold">Obstáculos agregados ({themeForm.obstacleImages.length}):</label>
                                        <div className="d-flex flex-wrap gap-2 border bg-white p-2 rounded min-vh-10">
                                            {themeForm.obstacleImages.map((img, i) => (
                                                <div key={i} className="position-relative border rounded p-1" style={{ width: '80px', height: '80px' }}>
                                                    <img src={getMediaUrl(img)} alt="Obstacle" className="w-100 h-100 object-fit-contain" />
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-danger btn-sm position-absolute top-0 end-0 p-0 d-flex align-items-center justify-content-center"
                                                        style={{ width: '20px', height: '20px', borderRadius: '50%' }}
                                                        onClick={() => removeObstacle(i)}
                                                    >
                                                        <i className="bi bi-x fs-6"></i>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <hr className="my-3" />

                                    {/* Configuración de Pantalla de Juego Interna (Cuadrito) */}
                                    <div className="col-12"><h6 className="fw-bold border-bottom pb-1 text-primary">Área de Juego (Cuadro Interno)</h6></div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Tipo de Fondo:</label>
                                        <select 
                                            className="form-select"
                                            value={themeForm.gameFrameBackgroundType}
                                            onChange={(e) => setThemeForm({ ...themeForm, gameFrameBackgroundType: e.target.value })}
                                        >
                                            <option value="color">Color plano</option>
                                            <option value="image">Imagen de fondo</option>
                                            <option value="video">Video de fondo</option>
                                        </select>
                                    </div>
                                    {themeForm.gameFrameBackgroundType === 'color' ? (
                                        <div className="col-md-8">
                                            <label className="form-label fw-bold">Color hexadecimal:</label>
                                            <input 
                                                type="color" 
                                                className="form-control form-control-color w-100" 
                                                value={themeForm.gameFrameBackgroundColor}
                                                onChange={(e) => setThemeForm({ ...themeForm, gameFrameBackgroundColor: e.target.value })}
                                            />
                                        </div>
                                    ) : (
                                        <div className="col-md-8">
                                            <DragAndDropField 
                                                label="Subir Fondo Juego" 
                                                targetField="gameFrameBackgroundUrl" 
                                                type={themeForm.gameFrameBackgroundType} 
                                                value={themeForm.gameFrameBackgroundUrl} 
                                                onUpload={handleUploadMedia} 
                                                getMediaUrl={getMediaUrl} 
                                            />
                                        </div>
                                    )}

                                    {/* Configuración de Portada */}
                                    <div className="col-12"><h6 className="fw-bold border-bottom pb-1 text-primary">Pantalla de Portada (Screen)</h6></div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Nombre del juego en portada:</label>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            value={themeForm.coverTitle} 
                                            onChange={(e) => setThemeForm({ ...themeForm, coverTitle: e.target.value })} 
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color del texto del título:</label>
                                        <input 
                                            type="color" 
                                            className="form-control form-control-color w-100" 
                                            value={themeForm.coverTitleColor} 
                                            onChange={(e) => setThemeForm({ ...themeForm, coverTitleColor: e.target.value })} 
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Tipo de Fondo Portada:</label>
                                        <select 
                                            className="form-select"
                                            value={themeForm.coverBackgroundType}
                                            onChange={(e) => setThemeForm({ ...themeForm, coverBackgroundType: e.target.value })}
                                        >
                                            <option value="color">Color plano</option>
                                            <option value="image">Imagen de fondo</option>
                                            <option value="video">Video de fondo</option>
                                        </select>
                                    </div>
                                    {themeForm.coverBackgroundType === 'color' ? (
                                        <div className="col-12">
                                            <label className="form-label fw-bold">Color hexadecimal:</label>
                                            <input 
                                                type="color" 
                                                className="form-control form-control-color w-100" 
                                                value={themeForm.coverBackgroundColor}
                                                onChange={(e) => setThemeForm({ ...themeForm, coverBackgroundColor: e.target.value })}
                                            />
                                        </div>
                                    ) : (
                                        <div className="col-12">
                                            <DragAndDropField 
                                                label="Subir Fondo Portada" 
                                                targetField="coverBackgroundUrl" 
                                                type={themeForm.coverBackgroundType} 
                                                value={themeForm.coverBackgroundUrl} 
                                                onUpload={handleUploadMedia} 
                                                getMediaUrl={getMediaUrl} 
                                            />
                                        </div>
                                    )}

                                    {/* Configuración de Aficionado */}
                                    <div className="col-12"><h6 className="fw-bold border-bottom pb-1 text-primary">Fondo Aficionado (/dinosaur)</h6></div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Tipo de Fondo Celular:</label>
                                        <select 
                                            className="form-select"
                                            value={themeForm.fanBackgroundType}
                                            onChange={(e) => setThemeForm({ ...themeForm, fanBackgroundType: e.target.value })}
                                        >
                                            <option value="color">Color plano</option>
                                            <option value="image">Imagen de fondo</option>
                                        </select>
                                    </div>
                                    {themeForm.fanBackgroundType === 'color' ? (
                                        <div className="col-md-8">
                                            <label className="form-label fw-bold">Color plano:</label>
                                            <input 
                                                type="color" 
                                                className="form-control form-control-color w-100" 
                                                value={themeForm.fanBackgroundColor}
                                                onChange={(e) => setThemeForm({ ...themeForm, fanBackgroundColor: e.target.value })}
                                            />
                                        </div>
                                    ) : (
                                        <div className="col-md-8">
                                            <DragAndDropField 
                                                label="Subir Imagen Fondo Celular" 
                                                targetField="fanBackgroundUrl" 
                                                type="background" 
                                                value={themeForm.fanBackgroundUrl} 
                                                onUpload={handleUploadMedia} 
                                                getMediaUrl={getMediaUrl} 
                                            />
                                        </div>
                                    )}

                                    {/* Colores de Textos y Bordes */}
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Color de Textos Generales:</label>
                                        <input 
                                            type="color" 
                                            className="form-control form-control-color w-100" 
                                            value={themeForm.textColors} 
                                            onChange={(e) => setThemeForm({ ...themeForm, textColors: e.target.value })} 
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Color de Bordes del Cuadro:</label>
                                        <input 
                                            type="color" 
                                            className="form-control form-control-color w-100" 
                                            value={themeForm.borderColors} 
                                            onChange={(e) => setThemeForm({ ...themeForm, borderColors: e.target.value })} 
                                        />
                                    </div>

                                    {/* Configuración del Botón del Aficionado */}
                                    <div className="col-12"><h6 className="fw-bold border-bottom pb-1 text-primary mt-2">Botón del Aficionado (/dinosaur)</h6></div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Tipo de Botón:</label>
                                        <select 
                                            className="form-select"
                                            value={themeForm.fanButtonType}
                                            onChange={(e) => setThemeForm({ ...themeForm, fanButtonType: e.target.value })}
                                        >
                                            <option value="color">Color plano</option>
                                            <option value="image">Imagen personalizada</option>
                                        </select>
                                    </div>
                                    {themeForm.fanButtonType === 'color' ? (
                                        <div className="col-md-8">
                                            <label className="form-label fw-bold">Color del Botón:</label>
                                            <input 
                                                type="color" 
                                                className="form-control form-control-color w-100" 
                                                value={themeForm.fanButtonColor}
                                                onChange={(e) => setThemeForm({ ...themeForm, fanButtonColor: e.target.value })}
                                            />
                                        </div>
                                    ) : (
                                        <div className="col-md-8">
                                            <DragAndDropField 
                                                label="Subir Imagen del Botón (PNG transparente redondo)" 
                                                targetField="fanButtonUrl" 
                                                type="background" 
                                                value={themeForm.fanButtonUrl} 
                                                onUpload={handleUploadMedia} 
                                                getMediaUrl={getMediaUrl} 
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer bg-light border-top">
                                <button className="btn btn-secondary px-4 fw-bold" onClick={() => setShowThemeModal(false)}>Cancelar</button>
                                <button className="btn btn-success px-4 fw-bold shadow-sm" onClick={handleSaveTheme}>
                                    <i className="bi bi-save me-1"></i> Guardar Tema
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* CONFIRMAR ACCIONES */}
            {showConfirmModal && (
                <Confirm 
                    message={confirmConfig.message} 
                    onYes={confirmConfig.action} 
                    onNo={() => setShowConfirmModal(false)} 
                    textYes={confirmConfig.textYes}
                    textNo={confirmConfig.textNo}
                />
            )}

            {/* MODAL: QR CODES */}
            {showQRModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
                    <div className="modal-dialog modal-dialog-centered text-dark">
                        <div className="modal-content border-0 shadow-lg rounded-3">
                            <div className="modal-header bg-dark text-white py-3">
                                <h5 className="modal-title fw-bold"><i className="bi bi-qr-code me-2"></i> Instrucciones de conexión</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowQRModal(false)}></button>
                            </div>
                            <div className="modal-body p-4 bg-light">
                                <PremiumQR 
                                    title={qrTitle} 
                                    url={qrTargetUrl} 
                                />
                            </div>
                            <div className="modal-footer bg-light border-top">
                                <button className="btn btn-secondary px-4 fw-bold" onClick={() => setShowQRModal(false)}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </SecondaryBase>
    );
};
